import { useBooking } from './ctx'
import { ADDON_PRICES, PROHIBITED, PURPOSES } from './constants'
import { ErrorText, Ic, Note, NumStepper, OptionCards } from './fields'
import type { Card } from './fields'
import { countryName, customsValue, dialCode } from './payload'
import { actualKg, addrLine, fmtDay, formKind, fromISO, naira, needsItems, num, resolveParty } from './rules'
import type { Price, Step } from './types'

export type FinalState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; price: Price }
  | null

const fixCap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '')

export function FinalNote({ final, estimate, onRetry }: { final: FinalState; estimate: Price | null; onRetry: () => void }) {
  if (!final) return null
  if (final.status === 'loading')
    return (
      <Note tone='info' icon='loader-circle' role='status'>
        <p>Getting the final price for these addresses and date…</p>
      </Note>
    )
  if (final.status === 'error')
    return (
      <Note tone='bad' icon='circle-alert'>
        <p>
          We couldn’t get the final price, so the booking can’t be made yet. {final.message && <>({final.message}) </>}
          <button type='button' className='bk-linkbtn inline' onClick={onRetry}>
            Try again
          </button>
        </p>
      </Note>
    )
  const total = num(final.price.totalPrice)
  const est = estimate ? num(estimate.totalPrice) : 0
  const diff = Math.round((total - est) * 100) / 100
  const extras = (final.price.breakDown?.extraCharges || []).map((c) => fixCap(c.name)).filter(Boolean)
  if (!est || Math.abs(diff) < 1)
    return (
      <Note tone='ok' icon='circle-check'>
        <p>
          <b>Final price: {naira(total)}</b>
          {est ? ' — the same as the estimate.' : '.'}
        </p>
      </Note>
    )
  return (
    <Note tone='warn' icon='info'>
      <p>
        <b>Final price is {naira(total)}</b> — {naira(Math.abs(diff))} {diff > 0 ? 'more' : 'less'} than the {naira(est)}{' '}
        estimate, now that the full addresses and date are known
        {diff > 0 && extras.length ? ` (includes ${extras.join(', ')})` : ''}.
      </p>
    </Note>
  )
}

function PayBlock({ final, onRefreshBalance, refreshing }: { final: FinalState; onRefreshBalance: () => void; refreshing: boolean }) {
  const { f, set, err, clearErr, customer } = useBooking()
  const ready = final?.status === 'ok'
  const total = ready ? num(final.price.totalPrice) : 0
  const c = customer
  const opts: Card[] = []
  const short = !!c && ready && c.balance < total
  opts.push({
    value: 'wallet',
    title: 'Customer’s wallet',
    icon: 'wallet',
    disabled: !c || !ready || short,
    sub: !c
      ? 'Choose a customer first'
      : short
        ? `Balance ${naira(c.balance)} — ${naira(total - c.balance)} short.${
            c.account_number ? ` They can top up by transfer to ${c.account_number}${c.bank ? ` (${c.bank})` : ''}.` : ''
          }`
        : `Balance ${naira(c.balance)}`,
  })
  opts.push({
    value: 'offline',
    title: 'Paid offline',
    icon: 'banknote',
    disabled: !ready,
    sub: 'Cash, transfer or POS at the counter. Nothing is taken from the wallet.',
  })
  if (c?.turnoff_payment) {
    const left = c.creditLimit - c.debt
    const over = ready && left < total
    opts.push({
      value: 'credit',
      title: 'Credit',
      icon: 'hand-coins',
      disabled: !ready || over,
      sub: over
        ? `${naira(Math.max(0, left))} left of ${naira(c.creditLimit)} — not enough for this booking`
        : `${naira(Math.max(0, left))} of ${naira(c.creditLimit)} available`,
    })
  }
  return (
    <>
      <OptionCards
        name='pay'
        legend='How is the customer paying?'
        cols={1}
        value={f.pay}
        options={opts}
        error={err('pay')}
        onChange={(v) => {
          set((d) => void (d.pay = v as 'wallet'))
          clearErr('pay')
        }}
      />
      {short && (
        <button type='button' className='bk-btn secondary sm mb' onClick={onRefreshBalance} disabled={refreshing} aria-busy={refreshing}>
          <Ic name='refresh-cw' className={refreshing ? 'spin' : ''} /> {refreshing ? 'Checking balance…' : 'They’ve topped up — refresh balance'}
        </button>
      )}
      <div className='bk-pay-sum'>
        <span>Total to pay</span>
        <b>{ready ? naira(total) : final?.status === 'error' ? '—' : 'Checking…'}</b>
      </div>
    </>
  )
}

export default function ReviewStep(p: {
  final: FinalState
  estimate: Price | null
  onRetry: () => void
  goTo: (s: Step) => void
  onRefreshBalance: () => void
  refreshing: boolean
}) {
  const { f, set, err, clearErr, customer, countries } = useBooking()
  const k = formKind(f)
  const snd = resolveParty(f, 'sender', customer)
  const rcv = resolveParty(f, 'receiver', customer)
  const pc = f.parcels.length
  const purpose = PURPOSES.find((x) => x.value === f.purpose)?.label
  const handover =
    f.handover.mode === 'pickup'
      ? `${k === 'import' ? 'DHL collects' : 'Rider collects'}${f.handover.date ? ` ${fmtDay(fromISO(f.handover.date))}` : ''}`
      : f.handover.mode === 'dropoff'
        ? k === 'import'
          ? 'Sender drops it at DHL'
          : 'Drop-off at a station'
        : ''
  const card = (icon: string, title: string, lines: (string | false | undefined)[], step: Step) => (
    <div className='bk-rv-card'>
      <span className='oi'>
        <Ic name={icon} />
      </span>
      <div className='rb'>
        <h3>{title}</h3>
        {lines.filter(Boolean).map((l, n) => (
          <p key={n} className={n === 0 ? 'strong' : ''}>
            {l}
          </p>
        ))}
      </div>
      <button type='button' className='bk-btn quiet sm' onClick={() => p.goTo(step)}>
        Edit<span className='bk-sr'> {title.toLowerCase()}</span>
      </button>
    </div>
  )
  const protFee = f.type === 'document' ? 7000 : Math.max(10000, num(f.value) * 0.02)

  return (
    <>
      <div className='bk-final' aria-live='polite'>
        <FinalNote final={p.final} estimate={p.estimate} onRetry={p.onRetry} />
      </div>

      <div className='bk-grp'>
        <h3 className='bk-grp-title'>Check the shipment</h3>
        <div className='bk-rv'>
          {card(
            'package',
            'Shipment',
            [
              `${countryName(countries, f.from)} → ${countryName(countries, f.to)}${k === 'domestic' ? ` · ${f.fromState} → ${f.toState}` : ''}`,
              `${f.type === 'document' ? 'Documents' : 'Package'} · ${pc} parcel${pc > 1 ? 's' : ''} · ${actualKg(f).toFixed(1)} kg${
                f.type === 'package' ? ` · worth ${naira(num(f.value))}` : ''
              }`,
              f.contents,
            ],
            'route',
          )}
          {card('user', 'Sender', [`${snd.first} ${snd.last}${snd.mode === 'owner' ? ' (account holder)' : ''}`, addrLine(snd.addr), handover], 'sender')}
          {card(
            'map-pin',
            'Receiver',
            [`${rcv.first} ${rcv.last}${rcv.mode === 'owner' ? ' (account holder)' : ''}`, addrLine(rcv.addr), rcv.phone && `${dialCode(countries, f.to)} ${rcv.phone}`],
            'receiver',
          )}
          {needsItems(f) &&
            card(
              'list',
              'Items',
              [
                f.items.map((i) => `${i.qty} × ${i.desc}`).join(', '),
                `Customs value ${f.currency === 'NGN' ? naira(customsValue(f)) : `${f.currency} ${customsValue(f).toLocaleString()}`}${purpose ? ` · ${purpose}` : ''}`,
              ],
              'items',
            )}
        </div>
      </div>

      <fieldset className='bk-grp'>
        <legend>Packaging</legend>
        <p className='bk-intro'>Packed for the customer at collection or drop-off.</p>
        {(
          [
            ['bubble', 'Bubble wrap', 'per wrap'],
            ['vacuum', 'Vacuum seal', 'per bag'],
          ] as const
        ).map(([key, name, unit]) => (
          <div className='bk-addon' key={key}>
            <div className='ad-t'>
              <b id={`ad-${key}-l`}>{name}</b>
              <span>
                {naira(ADDON_PRICES[key])} {unit}
              </span>
            </div>
            <NumStepper
              id={`ad-${key}`}
              labelledBy={`ad-${key}-l`}
              label={name.toLowerCase()}
              value={f.extras[key]}
              onChange={(v) => set((d) => void (d.extras[key] = Math.max(0, parseInt(v, 10) || 0)))}
            />
          </div>
        ))}
        <label className='bk-check'>
          <input type='checkbox' checked={f.extras.phyto} onChange={(e) => set((d) => void (d.extras.phyto = e.target.checked))} />
          <span>
            <b>Phytosanitary certificate · {naira(ADDON_PRICES.phyto)}</b>
            <span className='sub'>Needed for plants, seeds, grains and some food produce.</span>
          </span>
        </label>
      </fieldset>

      <fieldset className='bk-grp'>
        <legend>Shipment protection</legend>
        <div className='bk-protect'>
          <label className='bk-check'>
            <input type='checkbox' checked={f.extras.protect} onChange={(e) => set((d) => void (d.extras.protect = e.target.checked))} />
            <span>
              <b>Protect against loss or damage</b>
              <span className='sub'>
                {f.type === 'document' ? 'Flat ₦7,000 for documents.' : `2% of the ${naira(num(f.value))} value, minimum ₦10,000.`}
              </span>
            </span>
            <span className='price'>{naira(protFee)}</span>
          </label>
        </div>
      </fieldset>

      <PayBlock final={p.final} onRefreshBalance={p.onRefreshBalance} refreshing={p.refreshing} />

      <div className='bk-grp'>
        <div className='bk-declare' aria-invalid={err('declare') ? true : undefined}>
          <label className='bk-check'>
            <input
              type='checkbox'
              id='declare'
              checked={f.declare}
              aria-invalid={err('declare') ? true : undefined}
              aria-describedby={err('declare') ? 'declare-err' : undefined}
              onChange={(e) => {
                set((d) => void (d.declare = e.target.checked))
                clearErr('declare')
              }}
            />
            <span>
              <b>The customer confirms these details are accurate and the parcel has no prohibited items.</b>
            </span>
          </label>
          <details className='bk-more'>
            <summary>
              <Ic name='chevron-right' /> See what we can’t carry
            </summary>
            <ul>
              {PROHIBITED.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </details>
        </div>
        <ErrorText id='declare' msg={err('declare')} />
      </div>
    </>
  )
}
